import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";

const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [orgId, setOrgId] = useState(
    JSON.parse(localStorage.getItem("org")) || "",
  );
  const [role, setRole] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const jwtRole = storedUser?.userRole?.[0];

  // The backend resolves the caller's role per organization from their membership,
  // so this endpoint answers for admins and non-admins alike. An error here means
  // the user has no access to this organization - it must never be read as ADMIN.
  const fetchRole = async (selectedOrgId) => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${storedUser.username}&orgId=${selectedOrgId}`,
      {
        headers: {
          Authorization: `Bearer ${storedUser.accessToken}`,
        },
      },
    );

    return res.data.userRoleName;
  };

  // Runs when /dashboard initially opens
  useEffect(() => {
    const initializeOrganization = async () => {
      if (
        storedUser?.userRole?.[0] === "SUPERADMIN" ||
        storedUser?.userRole?.[0] === "PARTNER"
      ) {
        setRole(storedUser?.userRole?.[0]);
        setIsInitializing(false);
        return;
      }
      if (!orgId || !storedUser?.accessToken) {
        setIsInitializing(false);
        return;
      }

      try {
        const userRole = await fetchRole(orgId);

        setRole(userRole);
      } catch (error) {
        console.error("Failed to initialize organization:", error);
        setRole(null);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeOrganization();
  }, []);

  // Called from Navbar when organization changes
  const changeOrganization = async (newOrgId) => {
    if (!newOrgId || newOrgId === orgId) return;

    setIsSwitching(true);

    try {
      // First get the role for NEW organization
      const userRole = await fetchRole(newOrgId);

      // Clear old project
      localStorage.removeItem("projectId");

      // Store new organization
      localStorage.setItem("org", JSON.stringify(newOrgId));

      // Update Context
      setOrgId(newOrgId);
      setRole(userRole);
    } catch (error) {
      console.error("Failed to switch organization:", error);
      throw error;
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        orgId,
        role,
        isInitializing,
        isSwitching,
        changeOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => useContext(OrganizationContext);
